import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { createExperienceOrder, getAdminExperiences } from '../../actions/experienceAuth';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const CreateOrderModal = ({ open, handleClose, onSuccess }) => {
  const dispatch = useDispatch();

  const [experiences, setExperiences] = useState([]);
  const [selectedExpId, setSelectedExpId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [slots, setSlots] = useState(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(
        getAdminExperiences(
          { page: 0, size: 50, active: true },
          (res) => {
            const data = res?.data?.data || res?.data || [];
            const list = Array.isArray(data) ? data : data?.content || data?.experiences || [];
            setExperiences(list);
            if (list.length > 0 && !selectedExpId) {
              setSelectedExpId(list[0].id || list[0]._id);
            }
          },
          () => {}
        )
      );
    }
  }, [open, dispatch, selectedExpId]);

  const selectedExp = experiences.find((x) => (x.id || x._id) === selectedExpId);
  const unitPrice = selectedExp ? Number(selectedExp.discountPrice || selectedExp.pricing || selectedExp.price || 0) : 0;
  const estimatedTotal = unitPrice * Number(slots || 1);

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!selectedExpId) {
      enqueueSnackbar('Please select an experience', { variant: 'error' });
      return;
    }
    if (!customerName.trim()) {
      enqueueSnackbar('Customer name is required', { variant: 'error' });
      return;
    }
    if (!customerPhone.trim() && !customerEmail.trim()) {
      enqueueSnackbar('Customer email or phone is required', { variant: 'error' });
      return;
    }
    if (!bookingDate) {
      enqueueSnackbar('Please select a booking date', { variant: 'error' });
      return;
    }
    if (!slots || Number(slots) <= 0) {
      enqueueSnackbar('Please enter valid slots count', { variant: 'error' });
      return;
    }

    setLoading(true);

    const payload = {
      experienceId: selectedExpId,
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      customerPhone: customerPhone.trim(),
      bookingDate,
      slots: Number(slots),
      totalAmount: estimatedTotal,
      notes: notes.trim(),
    };

    dispatch(
      createExperienceOrder(
        payload,
        () => {
          setLoading(false);
          enqueueSnackbar('Order created successfully with status PENDING_FOR_APPROVAL', {
            variant: 'success',
          });
          handleClose();
          if (onSuccess) onSuccess();
        },
        (err) => {
          setLoading(false);
          const msg = err?.response?.data?.message || err?.message || 'Failed to create order';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: 'linear-gradient(135deg, #0c3483 0%, #11409B 50%, #1e40af 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ letterSpacing: '0.08em', opacity: 0.85, fontWeight: 700 }}>
            CREATE BOOKING
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.3 }}>
            New Experience Order
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#fff',
            bgcolor: alpha('#fff', 0.12),
            '&:hover': { bgcolor: alpha('#fff', 0.24) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5, bgcolor: '#f8fafc' }}>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
          Create an experience booking on behalf of a customer. Order will initially enter the{' '}
          <strong style={{ color: '#d97706' }}>PENDING_FOR_APPROVAL</strong> status.
        </Typography>

        <Grid container spacing={2}>
          {/* Experience Select */}
          <Grid item xs={12}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Experience *</InputLabel>
              <Select
                value={selectedExpId}
                label="Experience *"
                onChange={(e) => setSelectedExpId(e.target.value)}
                disabled={loading}
              >
                {experiences.map((exp) => (
                  <MenuItem key={exp.id || exp._id} value={exp.id || exp._id}>
                    {exp.title || exp.name} (₹{exp.discountPrice || exp.pricing || exp.price})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Customer Name */}
          <Grid item xs={12}>
            <TextField
              label="Customer Full Name *"
              placeholder="e.g. John Doe"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Customer Email & Phone */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email Address"
              placeholder="john@example.com"
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone Number"
              placeholder="+91 9876543210"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Booking Date & Slots */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Booking Date *"
              type="date"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Slots Booked *"
              type="number"
              value={slots}
              onChange={(e) => setSlots(Math.max(1, Number(e.target.value)))}
              fullWidth
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              label="Special Requests / Notes"
              placeholder="Dietary preferences, pickup requirements, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>

          {/* Estimated Total Calculation */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: '#fff',
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                Estimated Order Total ({slots} slots × ₹{unitPrice}):
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>
                ₹{estimatedTotal.toLocaleString('en-IN')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3.5,
            background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            boxShadow: '0 4px 16px rgba(17, 64, 155, 0.25)',
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Place Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrderModal;
